import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/context/LanguageContext";
import { formatPrice, formatDate } from "@/lib/format";
import api from "@/lib/api";

export default function NotificationBell() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/notifications")
      .then((r) => { setItems(r.data.requests || []); setCount(r.data.count || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && count > 0) {
      setCount(0);
      try { await api.post("/admin/notifications/mark-seen"); } catch { /* ignore */ }
    }
  };

  const goToRequest = () => { setOpen(false); navigate("/admin/requests"); };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="admin-notification-bell" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-gold text-[10px] font-semibold text-black leading-none"
              data-testid="notification-badge"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-none" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="font-serif text-lg">{es ? "Nuevas solicitudes" : "New requests"}</p>
          <Bell className="h-4 w-4 text-gold" />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              {es ? "No hay solicitudes nuevas." : "No new requests."}
            </div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={goToRequest}
                className="w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/50 transition-colors"
                data-testid={`notification-item-${it.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{it.order_number}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(it.created_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                  <Inbox className="h-3 w-3" /> {it.name || (es ? "Cliente" : "Client")} · {formatPrice(it.subtotal)}
                </div>
              </button>
            ))
          )}
        </div>
        <button
          onClick={goToRequest}
          className="w-full px-4 py-3 border-t border-border text-xs uppercase tracking-widest text-gold hover:bg-secondary/50 transition-colors"
          data-testid="notification-view-all"
        >
          {es ? "Ver todas las solicitudes" : "View all requests"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
